import { IconUser, IconUserBolt } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

export interface TeammateLabelProps {
  name: string;
  kind: "human" | "voiceAgent";
  /** Shows the name alone, without the initials or voice-agent icon. */
  isAvatarHidden?: boolean;
}

/** First letters of the name's first and last words, e.g. "Jane Q. Smith" gives "JS". */
function buildInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words.at(0)?.charAt(0) ?? "";
  const last = words.length > 1 ? (words.at(-1)?.charAt(0) ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

/** A teammate's avatar and name: initials for people, an icon for voice agents. */
export const TeammateLabel: FC<TeammateLabelProps> = (props) => {
  const { name, kind, isAvatarHidden = false } = props;

  return (
    <span className="inline-flex items-center gap-1.5">
      {!isAvatarHidden && (
        <span
          aria-hidden="true"
          className={twMerge(
            "inline-flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-md text-2xs font-medium",
            kind === "voiceAgent"
              ? "bg-violet-50 text-violet-700"
              : "bg-blue-50 text-blue-700",
          )}
        >
          {kind === "voiceAgent" ? (
            <IconUserBolt className="size-3.5" />
          ) : (
            buildInitials(name) || <IconUser className="size-3.5" />
          )}
        </span>
      )}
      <span className="font-medium text-foreground">{name}</span>
      {kind === "voiceAgent" && <span className="sr-only">(voice agent)</span>}
    </span>
  );
};
